/**
 * Shared canonical search filter — the single client-side match gate for
 * every ToolSight tool-search surface (S1/S2/S3/S6 sub-min fallback,
 * S8/S9 mobile offline). Mirrors the backend `search_inventory` predicate
 * over the canonical Tools set (searchFields.js, kept in sync with
 * search_config.py SEARCH_FIELDS_CANONICAL) + joined part_numbers/asset_tags.
 *
 * Why a `_match_context` clause exists (Session C design decision):
 * `search_inventory` returns raw tool docs annotated with `_match_context`
 * for asset-tag / part-number hits, but does NOT attach part_numbers[] /
 * asset_tags[] arrays. So a tool the server matched via an asset tag has
 * neither the query in a canonical field nor the arrays. Without the
 * `_match_context` clause, running this gate over server results would
 * wrongly drop exactly the rows the original bug was about. With it, this
 * function is correct as the universal gate over BOTH client lists (which
 * carry the arrays) and server results (which carry `_match_context`).
 */

import { SEARCH_FIELDS_TOOLS, MIN_QUERY_LENGTH } from './searchFields';

function coerce(v) {
  if (v === null || v === undefined) return '';
  return String(v).toLowerCase();
}

/**
 * Canonical search filter — shared across all client-side filtering surfaces.
 *
 * @param {object} tool - Tool object (raw snake_case OR transformed camelCase)
 * @param {string} query - User's search text
 * @param {object} [options]
 * @param {object} [options.fieldNameMap] - Optional mapping from canonical
 *   snake_case names to surface-specific names, e.g.
 *   { supplier_tool_id: 'toolId', site_location: 'siteLoc' }. Canonical
 *   fields absent from the map are read as-is (snake_case).
 * @param {string[]} [options.extraFields] - Surface-specific extra fields to
 *   also match, named as they appear on `tool` (e.g. ['bolNbr','truckNbr']
 *   for Disposal Portal). Not part of the canonical set.
 * @returns {boolean} true if the tool matches (or query is below the
 *   minimum length / empty — "show all").
 */
export function matchesCanonicalSearch(tool, query, options = {}) {
  if (!tool) return false;
  const q = (query || '').trim().toLowerCase();
  if (q.length < MIN_QUERY_LENGTH) return true; // show-all below threshold

  const map = options.fieldNameMap || {};

  // 1. Canonical Tools fields (defensive coercion handles numeric fields
  //    like tier_level).
  for (const canonical of SEARCH_FIELDS_TOOLS) {
    const key = map[canonical] || canonical;
    if (coerce(tool[key]).includes(q)) return true;
  }

  // 2. Surface-specific extra fields (already named as on `tool`).
  if (Array.isArray(options.extraFields)) {
    for (const f of options.extraFields) {
      if (coerce(tool[f]).includes(q)) return true;
    }
  }

  // 3. Joined part numbers / asset tags (snake_case pass-through on every
  //    surface that carries them; allow a remap just in case).
  const pnKey = map.part_numbers || 'part_numbers';
  const atKey = map.asset_tags || 'asset_tags';
  const pns = tool[pnKey];
  if (Array.isArray(pns) && pns.some(pn => coerce(pn).includes(q))) return true;
  const ats = tool[atKey];
  if (Array.isArray(ats) && ats.some(at => coerce(at).includes(q))) return true;

  // 4. Backend match-context (server results from search_inventory — see
  //    header note). Field is 'asset_tag' | 'part_number'; value is the
  //    matched string.
  const mc = tool._match_context;
  if (mc && coerce(mc.value).includes(q)) return true;

  return false;
}
